import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';

import useFormValidation from 'hooks/useFormValidation';
import useFileUpload from 'hooks/useFileUpload';
import FileUploader from 'components/common/FileUploader';

import { adminWarehouseApi, adminShelfApi } from 'api';

const validationSchema = {
  name: {
    required: 'Name is required',
  },
  code: {
    required: 'Code type is required',
  },
  warehouse: {
    required: 'Warehouse is required',
  },
};

const initialValues = {
  name: '',
  code: '',
  warehouse: '',
  description: '',
  priority: '0',
};

const CreateShelfModal = ({ show, onHide, onSuccess }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [isLoadingWarehouses, setIsLoadingWarehouses] = useState(false);
  const [warehousesError, setWarehousesError] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    resetForm: resetFormValidation,
    isValid,
  } = useFormValidation(initialValues, validationSchema);

  const { file, getRootProps, getInputProps, resetFile } = useFileUpload();

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoadingWarehouses(true);

      try {
        const response = await adminWarehouseApi.getList();
        setWarehouses(response.results || []);
      } catch (error) {
        toast.error('Failed to load warehouses');
        setWarehousesError('Failed to load warehouses');
      } finally {
        setIsLoadingWarehouses(false);
      }
    };

    if (show) {
      fetchInitialData();
    }
  }, [show]);

  const resetForm = useCallback(() => {
    resetFormValidation();
    resetFile();
  }, [resetFormValidation, resetFile]);

  const handleCreateShelf = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('code', values.code);
      formData.append('warehouse', Number(values.warehouse));

      if (file) {
        formData.append('location_image', file);
      }

      if (values.description) {
        formData.append('description', values.description);
      }

      if (values.priority !== '') {
        formData.append('priority', Number(values.priority));
      }

      await adminShelfApi.createForm(formData);

      toast.success('Shelf created successfully');
      closeModal();
      onSuccess();
    } catch (error) {
      toast.error('Failed to create shelf');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = useCallback(() => {
    resetForm();
    onHide();
  }, [resetForm, onHide]);

  const renderSelectOptions = (isLoading, error, options, optionName) => {
    if (isLoading) {
      return <option value="">Loading...</option>;
    }
    if (error) {
      return <option value="">{error}</option>;
    }

    return options.map(option => (
      <option key={option.id} value={option.id}>
        {option[optionName]}
      </option>
    ));
  };

  return (
    <Modal show={show} onHide={closeModal}>
      <Modal.Header closeButton>
        <Modal.Title>Create Shelf</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              Name<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="input"
              value={values.name}
              onChange={e => handleChange('name', e.target.value)}
              placeholder="Enter Name..."
              onBlur={() => handleBlur('name')}
              isInvalid={touched.name && errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Code<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="input"
              value={values.code}
              onChange={e => handleChange('code', e.target.value)}
              placeholder="Enter Code..."
              onBlur={() => handleBlur('code')}
              isInvalid={touched.code && errors.code}
            />
            <Form.Control.Feedback type="invalid">
              {errors.code}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Warehouse<span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={values.warehouse}
              onChange={e => handleChange('warehouse', e.target.value)}
              onBlur={() => handleBlur('warehouse')}
              isInvalid={touched.warehouse && errors.warehouse}
              disabled={isLoadingWarehouses}
            >
              <option value="">Select Warehouse...</option>
              {renderSelectOptions(
                isLoadingWarehouses,
                warehousesError,
                warehouses,
                'name',
              )}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.warehouse}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Priority</Form.Label>
            <Form.Control
              type="number"
              value={values.priority}
              onChange={e => handleChange('priority', e.target.value)}
              placeholder="Enter Priority..."
            />
            <Form.Text className="text-muted">
              * Batch picking walk order. Higher priority shelves are visited
              first.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Location Image</Form.Label>
            <FileUploader
              file={file}
              getRootProps={getRootProps}
              getInputProps={getInputProps}
              resetFile={resetFile}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={values.description}
              onChange={e => handleChange('description', e.target.value)}
              placeholder="Enter Description..."
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={resetForm}>
          Clear
        </Button>
        <Button
          variant="primary"
          onClick={handleCreateShelf}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? 'Creating...' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateShelfModal;
